<template>
  <div
    v-click-outside="() => hideMenu()"
    class="notifications-menu"
  >
    <div @click="toggleDropdown">
      <AuIcon
        icon="notification"
        :clickable="true"
      />
      <span
        v-if="unreadCount > 0"
        class="notification-badge"
      >
        {{ unreadCount }}
      </span>
    </div>

    <div
      v-if="isOpen"
      class="notifications-container"
    >
      <div class="notifications-header">
        <h3>{{ $t('notifications.title') }}</h3>
        <div
          v-if="unreadCount > 0"
          class="mark-all-read"
          @click="markAllAsRead"
        >
          <AuIcon
            class="mark-all-read__icon"
            path="notifications"
            icon="check"
          />
          <span>
            {{ $t('notifications.markAllAsRead') }}
          </span>
        </div>
      </div>

      <div class="notifications-scrollable">
        <div
          v-if="notifications.length === 0"
          class="empty-notifications"
        >
          {{ $t('notifications.empty') }}
        </div>

        <ul
          v-else
          class="notifications-list"
        >
          <li
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            @click="markAsRead(notification.id)"
          >
            <AuIcon
              class="notification-type-icon"
              :class="[`type-${notification.type}`]"
              :icon="notification.type"
              path="notifications"
            />

            <div>
              <div class="notification">
                <span>
                  {{ notification.content }}
                </span>
                <span class="notification-time">
                  {{ formatTimeAgo(notification.date) }}
                </span>
              </div>

              <div>
                <div
                  v-if="!notification.shown"
                  class="unread-dot"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import notificationsController from "@/help/notificationsController";

export default {
  data() {
    return {
      isOpen: false,
      notifications: []
    };
  },

  computed: {
    ...mapGetters({
      checkProfile: "profile/checkProfile",
      isLightTheme: "isLightTheme"
    }),

    unreadCount() {
      return this.notifications.filter(n => !n.shown).length;
    }
  },

  watch: {
    checkProfile() {
      this.loadNotifications();
    }
  },

  created() {
    this.loadNotifications();
  },

  methods: {
    loadNotifications() {
      const savedNotifications = notificationsController.getNotifications(this.$auth.user().id);
      this.notifications = savedNotifications ? savedNotifications.reverse() : [];
    },

    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.loadNotifications();
      }
    },

    markAsRead(id) {
      notificationsController.markNotificationAsRead(this.$auth.user().id, id);
      this.loadNotifications();
    },

    markAllAsRead() {
      notificationsController.markNotificationsAsRead(this.$auth.user().id);
      this.loadNotifications();
    },

    formatTimeAgo(date) {
      if (!date) {
        return "";
      }

      const now = new Date();
      const formatedDate = new Date(date);
      const diffInSeconds = Math.floor((now - formatedDate) / 1000);

      if (diffInSeconds < 5) {
        return this.$t("notifications.date.justNow");
      }
      else if (diffInSeconds < 60) {
        return this.$t("notifications.date.secondsAgo", { count: diffInSeconds });
      }
      else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return this.$t("notifications.date.minutesAgo", { count: minutes });
      }
      else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return this.$t("notifications.date.hoursAgo", { count: hours });
      }
      else if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return this.$t("notifications.date.daysAgo", { count: days });
      }
      else if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return this.$t("notifications.date.monthsAgo", { count: months });
      }
      else {
        const years = Math.floor(diffInSeconds / 31536000);
        return this.$t("notifications.date.yearsAgo", { count: years });
      }
    },

    hideMenu() {
      this.isOpen = false;
    }
  }
};
</script>

<style scoped lang="scss">
.notifications-menu {
  position: relative;
  display: inline-block;
}

.notifications-toggle {
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
}

.notification-icon {
  position: relative;
  display: inline-block;
  color: var(--text-color);
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #ff4757;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.notifications-container {
  position: absolute;
  right: 5px;
  top: 59px;
  min-width: 275px;
  max-width: 375px;
  width: 20vw;
  max-height: 400px;
  background-color: var(--menu-color);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  display: flex;
  flex-direction: column;
}

.notifications-header {
  display: flex;
  justify-content: space-between;

  padding: 15px 20px 15px;
  height: 50px;

  align-items: center;
}

.notifications-header h3 {
  margin: 0;
  font-size: 15px;
  color: var(--text-color);
}

.mark-all-read {
  display: flex;
  align-items: center;
  gap: 5px;

  background: none;
  border: none;
  color: #2aba7c;
  cursor: pointer;
  font-size: 13px;
  padding: 0;

  &__icon {
    height: 12px;
    filter: brightness(0) saturate(100%) invert(55%) sepia(95%) saturate(331%) hue-rotate(102deg) brightness(90%) contrast(91%);
  }

  &:hover {
    opacity: 0.9;
  }
}

.notifications-scrollable {
  overflow-y: auto;
  padding: 0 20px;
  flex-grow: 1;
}

.empty-notifications {
  padding: 10px 0;
  text-align: center;
  color: var(--color-60);
  font-size: 13px;
}

.notifications-list {
  list-style: none;
  padding: 0;
  margin: 0;

  border-top: 1px solid var(--color-70);
}

.notification-item {
  display: flex;
  gap: 8px;

  padding: 12px 0;
  cursor: pointer;
  border-bottom: 1px dashed var(--color-70);

  &:last-child {
    border-bottom: none;
  }

  >div {
    display: flex;
    justify-content: space-between;
    align-items: center;

    width: 100%;
    padding-right: 5px;
  }

  .type-info {
    filter: brightness(0) saturate(100%) invert(46%) sepia(85%) saturate(429%) hue-rotate(161deg) brightness(98%) contrast(91%);
  }

  .type-warning {
    filter: brightness(0) saturate(100%) invert(74%) sepia(22%) saturate(5372%) hue-rotate(352deg) brightness(98%) contrast(95%);
  }

  .type-error {
    filter: brightness(0) saturate(100%) invert(25%) sepia(91%) saturate(1689%) hue-rotate(343deg) brightness(116%) contrast(81%);
  }

  .type-success {
    filter: brightness(0) saturate(100%) invert(57%) sepia(94%) saturate(361%) hue-rotate(93deg) brightness(97%) contrast(87%);
  }
}

.notification {
  display: flex;
  flex-direction: column;

  font-size: 13px;
  color: var(--text-color);
  line-height: 1.3;
}

.notification-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.unread-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #5fc396;
  border-radius: 50%;
  cursor: pointer;
}

.notification-time {
  color: var(--color-60);
  font-size: 11px;
}

.notification-type {
  text-transform: capitalize;
}

.notification-item:hover {
  opacity: 0.9;
}
</style>